import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../cx.js';

export type VarianteAlert = 'success' | 'warning' | 'error' | 'info';

export interface AlertProps {
  variant: VarianteAlert;
  title: string;
  /** Testo in `ink`: spiega sempre il passo successivo. */
  children?: ReactNode;
  /** Azione opzionale (di solito un Button discreto o secondario). */
  action?: ReactNode;
  className?: string;
  /** `status` per avvisi, `alert` per errori che richiedono attenzione. */
  role?: 'status' | 'alert';
}

const stili: Record<VarianteAlert, { icona: LucideIcon; fondo: string; colore: string }> = {
  success: { icona: CircleCheck, fondo: 'bg-success-soft', colore: 'text-success' },
  warning: { icona: TriangleAlert, fondo: 'bg-warning-soft', colore: 'text-warning' },
  error: { icona: CircleAlert, fondo: 'bg-error-soft', colore: 'text-error' },
  info: { icona: Info, fondo: 'bg-accent-soft', colore: 'text-accent' },
};

/** Messaggio di esito: fondo tenue, icona e titolo nel colore pieno, testo in ink. Mai solo colore. */
export function Alert({ variant, title, children, action, className, role }: AlertProps) {
  const { icona: Icona, fondo, colore } = stili[variant];
  return (
    <div role={role ?? (variant === 'error' ? 'alert' : 'status')} className={cx('rounded-lg p-6 flex gap-3', fondo, className)}>
      <Icona aria-hidden size={24} strokeWidth={1.75} className={cx('shrink-0 mt-0.5', colore)} />
      <div className="flex flex-col gap-2 min-w-0">
        <p className={cx('t-title-3', colore)}>{title}</p>
        {children && <div className="t-body text-ink">{children}</div>}
        {action && <div className="pt-1">{action}</div>}
      </div>
    </div>
  );
}

/** Alias semantico per gli esiti (test, attestato, presenza). */
export const Outcome = Alert;
